import React, { useCallback, useMemo, useState } from 'react';

import cn from 'classnames';

import styles from './ClickableStatistic.module.scss';
import { IStatisticProps, Statistic } from './Statistic';
import { Card } from '../Card';

export interface IClickableStatisticProps extends IStatisticProps {
  onClick(): void;
  isActive: boolean;
  activeColor?: string;
  isDisabled?: boolean;
  disabledColor?: string;
  hoverColor?: string;
  cardClassName?: string;
}

enum DEFAULT_CARD_COLORS {
  HOVERED = '#f4fcff',
  ACTIVE = '#e6f7ff',
  DEFAULT = 'white',
}

enum DEFAULT_TEXT_COLORS {
  ACTIVE = '#167CF4',
  DISABLED = '#bfbfbf',
}

export const ClickableStatistic: React.FC<IClickableStatisticProps> = (
  props
) => {
  const {
    isActive,
    activeColor,
    isDisabled = false,
    hoverColor,
    cardClassName,
    valueStyle,
    onClick,
  } = props;

  const [isHovered, setHovered] = useState(false);

  const handleOnClick = useCallback(() => {
    if (!isDisabled && onClick) onClick();
  }, [isDisabled, onClick]);

  const getColor = useMemo(() => {
    if (isHovered) {
      return hoverColor ?? DEFAULT_CARD_COLORS.HOVERED;
    }
    if (isActive) {
      return activeColor ?? DEFAULT_CARD_COLORS.ACTIVE;
    }
    return DEFAULT_CARD_COLORS.DEFAULT;
  }, [isHovered, isActive, hoverColor, activeColor]);

  const getTextColor = useMemo(() => {
    if (isDisabled) return DEFAULT_TEXT_COLORS.DISABLED;
    if (isActive) return DEFAULT_TEXT_COLORS.ACTIVE;
    return 'black';
  }, [isActive, isDisabled]);

  const textColor =
    valueStyle &&
    Object.prototype.hasOwnProperty.call(valueStyle, 'color') &&
    valueStyle.color
      ? valueStyle.color
      : getTextColor;

  return (
    <Card
      onClick={handleOnClick}
      onMouseEnter={() => isDisabled === false && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: getColor,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
      className={cn(styles.clickableStatisticBoxCard, cardClassName)}
    >
      <Statistic
        {...props}
        valueStyle={{
          ...valueStyle,
          color: textColor,
        }}
      />
    </Card>
  );
};
